/**
 * What the login/register hero says, based on what the seeker just tried to do.
 *
 * A signed-out seeker who taps a gated action used to get one generic hero
 * ("See Beyond the Obvious" + a feature list) no matter what they clicked, and
 * the click itself was thrown away. Each gated action now raises a theme, the
 * hero sells that theme, and the replayable ones are re-run after login.
 *
 * Copy rules these strings are held to (see the spec):
 *  - short enough for the modal's 296px column at a 380px viewport
 *  - the product's word is "Mystic", never "reader"
 *  - no em dashes
 *  - plain language: a first-time visitor has not learned "Coven", "emeralds",
 *    "drops" or "unseal" yet, so those words stay out of the front door
 */

export const AUTH_THEMES = {
  // Revealing a locked card, or arriving on a shared reading.
  reading: {
    eyebrow: 'Your reading',
    headline: 'See the whole spread',
    lines: [
      'Every card, and the full answer to what you asked.',
      'Saved, so you can come back to it.',
    ],
  },

  // Hearting a reading, friend requests, replying to someone.
  social: {
    eyebrow: 'Your people',
    headline: 'Send it to someone',
    lines: [
      'Share a reading privately with a friend, and hear what they make of it.',
    ],
  },

  // Favouriting an image.
  collection: {
    eyebrow: 'Your collection',
    headline: 'Keep what you love',
    lines: [
      'Save any image, including the art from your own readings.',
    ],
  },

  // Following a Mystic, rating their deck.
  mystics: {
    eyebrow: 'Your Mystics',
    headline: 'Find them again',
    lines: [
      'Every Mystic you sit with is kept in your journal.',
    ],
  },

  // Voting, conjuring a candidate, talking to Mystic AIda.
  voice: {
    eyebrow: 'Your say',
    headline: 'Decide what gets built',
    lines: [
      'Vote on what comes next. The winner gets made.',
    ],
  },

  // The Altar tab. The one that carries the most weight: a daily reading cast
  // from the seeker's own chart has been a one-to-one, expensive thing forever.
  altar: {
    eyebrow: 'Your altar',
    headline: 'A place to come back to',
    lines: [
      'Words to ground your morning. A space that stays yours.',
      'A message written from the sky the day you were born.',
    ],
  },
}

/**
 * The hero for a seeker who opened login deliberately (the "Reveal Myself"
 * pill, the Studio route guard) rather than by bumping into a gate.
 */
export const DEFAULT_INTENT = {
  eyebrow: '',
  headline: 'See Beyond the Obvious',
  lines: [
    'Readings, horoscopes and a place of your own, kept as you go.',
  ],
}

/**
 * Actions that can be replayed from plain data after login.
 *
 * The post-login navigation re-enters handleRouteChange to re-establish the
 * session, which unmounts whatever component raised the gate. A resume
 * therefore cannot be a closure over component state. Everything listed here
 * is a single API call with serialisable arguments and no UI dependency.
 * Revealing a card, the shared-reading arrival and the reply composer all need
 * restored UI, so they are deliberately not here.
 */
export const RESUMABLE_ACTIONS = [
  'favorite-image',
  'follow-mystic',
  'request-friend',
  'accept-friend',
  'rate-deck',
  'vote-poll',
]

/**
 * The door after a reading made for someone (R3.8): it is titled with the
 * person the reading is for. Her name comes with the intent, so it is built
 * here rather than kept in AUTH_THEMES.
 */
export function sendDoorCopy(name) {
  return {
    eyebrow: `Your reading for ${name}`,
    headline: `Send it to ${name}`,
    lines: [`Make your place here, then send it to ${name}.`],
  }
}

/**
 * A members-only link opened signed out (navigation API §6): the server's
 * reason names what is behind it ("Sign in to read your messages"). The one
 * place the sign-in sheet opens by itself; "Start with a reading" takes a
 * newcomer to a reading instead, where nothing is asked before the draw.
 */
export function reasonCopy(reason = '') {
  const r = String(reason || '')
  if (/messages/i.test(r)) return { eyebrow: 'Your messages', headline: 'Sign in to read your messages', lines: ['Your messages are kept with your place here.'] }
  if (/quest/i.test(r)) return { eyebrow: 'Your quest', headline: 'Sign in to see your Quest', lines: ["Your Quest keeps what you've done here and what's next."], secondary: 'Start with a reading' }
  if (/studio/i.test(r)) return { eyebrow: 'The Studio', headline: 'Sign in to open your Studio', lines: ['The Studio is where a Mystic is made. Quest shows the way there.'] }
  if (/reading/i.test(r)) {
    return { eyebrow: 'Your altar', headline: 'Sign in to open your Altar',
      lines: ["Your Altar keeps your readings and your signs. Sign in, or start with a reading: it's kept for you as soon as you have one."],
      secondary: 'Start with a reading' }
  }
  return { eyebrow: '', headline: 'Sign in first', lines: ['Your place here keeps it for you.'] }
}

export function resolveAuthIntent(intent) {
  const theme = intent && intent.theme
  if (theme === 'reason') return reasonCopy(intent.reason)
  if (theme === 'send') {
    const name = ((intent && intent.forName) || '').trim()
    return name ? sendDoorCopy(name) : AUTH_THEMES.reading
  }
  return (theme && AUTH_THEMES[theme]) || DEFAULT_INTENT
}

/** Which door a reading's gate opens: the send door for a reading made for
 * someone (store.reading_gift), the reading door otherwise. */
export function doorIntentFor(gift) {
  const name = ((gift && gift.is_gift && gift.for_name) || '').trim()
  return name ? { theme: 'send', forName: name } : { theme: 'reading' }
}

/** R3.9: the send door offers "Email me a code" first, and picks it. */
export function codeFirst(intent) {
  // A members-only link is for someone who has a place: the code first.
  return !!(intent && (intent.theme === 'send' || intent.theme === 'reason'))
}

export function isResumable(resume) {
  return !!(resume && RESUMABLE_ACTIONS.includes(resume.action))
}
